/**
 * BandTag — the pill rendered next to every score.
 *
 * Per the SYR-68 decision, we keep our existing 6-band classification labels
 * (Excellent / Good / Neutral / Needs Attention / Priority Concern /
 * Unbelievably Strong) in the text, but collapse the colour to the 3-bucket
 * palette (strong/watch/weak/na) for visual impact.
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { Band } from '../types';
import { BAND_PALETTE } from '../config/bandColours';

export interface BandTagProps {
  /** Full 6-band label from the API (e.g. "Excellent", "Priority Concern"). */
  classification: string | null;
  /** Collapsed 3-bucket band for the colour. */
  band: Band;
  /** Override the displayed text (e.g. for hero "No data"). */
  labelOverride?: React.ReactNode;
}

export const BandTag: React.FunctionComponent<BandTagProps> = ({
  classification,
  band,
  labelOverride,
}) => {
  const palette = BAND_PALETTE[band];
  const label =
    labelOverride ??
    classification ??
    (band === 'na' ? (
      <FormattedMessage id="marketerView.band.noData" defaultMessage="No data" />
    ) : (
      <FormattedMessage id="marketerView.band.unknown" defaultMessage="—" />
    ));
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: palette.fg,
        background: palette.bg,
        border: `1px solid ${palette.line}`,
        padding: '2px 9px',
        borderRadius: 20,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};
